import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCanWithoutBuilding } from "@/lib/policy";
import { BioPromptCard } from "./-components/bio-prompt-card";
import { GuidanceSection, type Guidance } from "./-components/guidance-section";
import { GuidanceUpload, type GuidanceMediaKind } from "./-components/guidance-upload";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/stage-guidance")({
  component: StageGuidance,
});

const KIND = {
  video: "interviewer_video",
  intro: "team_intro",
  headshot: "headshot_example",
  prompt: "bio_prompt",
} as const;

const HEADSHOT_LIMIT = 3;
const fail = (error: Error) => toast.error(errorWords(error));

/** What an applicant is shown before they record their video and upload their headshot. */
function StageGuidance() {
  const { facilityId } = useFacility();
  const building = facilityId === "all" ? null : facilityId;
  const canManage = useCanWithoutBuilding("hiring:manage");
  const queryClient = useQueryClient();
  // A removed video or example is gone, so the press asks first.
  const [removing, setRemoving] = useState<Guidance | null>(null);

  const guidance = useQuery(
    orpc.workforce.hiringCatalog.listStageGuidance.queryOptions({ input: { facilityId } }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.hiringCatalog.key() });

  const remove = useMutation(
    orpc.workforce.hiringCatalog.deleteStageGuidance.mutationOptions({
      onSuccess: async () => {
        toast.success("Removed");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const makeDefault = useMutation(
    orpc.workforce.hiringCatalog.setDefaultStageGuidance.mutationOptions({
      onSuccess: async () => {
        toast.success("Default video set");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const addMedia = useMutation(
    orpc.workforce.hiringCatalog.saveStageGuidance.mutationOptions({
      onSuccess: async () => {
        toast.success("Uploaded");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const savePrompt = useMutation(
    orpc.workforce.hiringCatalog.saveStageGuidance.mutationOptions({
      onSuccess: async () => {
        toast.success("Prompt saved");
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = guidance.data ?? [];
  /* Uploading files belongs to one building — the video an applicant watches is
     the building they applied to — so it is offered only when one is picked. */
  const uploader = (kind: GuidanceMediaKind, nameLabel: string, extraLabel?: string) =>
    building ? (
      <GuidanceUpload
        facilityId={building}
        kind={kind}
        nameLabel={nameLabel}
        {...(extraLabel ? { extraLabel } : {})}
        disabled={addMedia.isPending}
        onUploaded={(uploaded) => addMedia.mutate({ facilityId: building, kind, ...uploaded })}
      />
    ) : null;
  const ofKind = (kind: string) => rows.filter((row) => row.kind === kind);
  const prompt = rows.find((row) => row.kind === KIND.prompt) ?? null;

  return (
    <>
      <PageHeader
        title="What applicants see"
        meta="The video candidates watch before recording, and the guidance shown beside their headshot and bio."
      />

      {canManage && !building ? (
        <p className="text-muted-foreground text-sm">
          Each building has its own videos, examples and bio prompt. Choose one in the building
          switcher at the top to add or change them.
        </p>
      ) : null}

      {guidance.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : guidance.isError ? (
        <ReadFailed
          message="Could not load what applicants see."
          error={guidance.error}
          retrying={guidance.isFetching}
          onRetry={() => void guidance.refetch()}
        />
      ) : (
        <div className="space-y-4">
          <GuidanceSection
            title="Interview videos"
            description="Applicants must watch one in full before they can record. A job can pick its own; the default is the fallback for jobs that do not."
            emptyText="No interview video yet. Without one, applicants go straight to recording. Nothing is blocked."
            rows={ofKind(KIND.video)}
            canManage={canManage}
            upload={uploader(KIND.video, "Title for this video")}
            onMakeDefault={(row) => makeDefault.mutate({ guidanceId: row.id })}
            onRemove={setRemoving}
          />

          <GuidanceSection
            title="Meet the team"
            description="Optional introductions applicants can watch. Nothing here is required."
            emptyText="No introductions yet. A short video from whoever a candidate would meet first goes here."
            rows={ofKind(KIND.intro)}
            canManage={canManage}
            upload={uploader(KIND.intro, "Person's name", "Their role")}
            onRemove={setRemoving}
          />

          <GuidanceSection
            title="Headshot examples"
            description={`Up to ${HEADSHOT_LIMIT} photos, shown beside the upload so candidates know what good looks like.`}
            emptyText="No examples yet. Candidates just see the upload button."
            rows={ofKind(KIND.headshot)}
            canManage={canManage}
            upload={
              ofKind(KIND.headshot).length >= HEADSHOT_LIMIT ? (
                <p className="text-muted-foreground pt-3 text-sm">
                  That is the {HEADSHOT_LIMIT} examples a candidate is shown. Remove one to add
                  another.
                </p>
              ) : (
                uploader(KIND.headshot, "What this example shows")
              )
            }
            onRemove={setRemoving}
          />

          <BioPromptCard
            key={prompt?.id ?? "none"}
            defaultBody={prompt?.body ?? ""}
            canEdit={canManage && Boolean(building)}
            fallbackText={
              building
                ? (prompt?.body ?? "No prompt set. Candidates see an empty bio box.")
                : "Pick one building to see and change its bio prompt."
            }
            pending={savePrompt.isPending}
            onSubmit={(body) => {
              if (!building) return;
              savePrompt.mutate({
                ...(prompt ? { guidanceId: prompt.id } : {}),
                facilityId: building,
                kind: KIND.prompt,
                body,
              });
            }}
          />
        </div>
      )}

      {removing ? (
        <RemoveConfirm
          title={`Remove ${removing.title || removing.fileName || "this item"}?`}
          description="Applicants stop seeing it straight away. To show it again, upload it again."
          confirmLabel="Remove"
          onConfirm={() => remove.mutate({ guidanceId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
